import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  getPaymentRecipient,
  listPaymentRecipients,
  patchPaymentRecipient,
  createPaymentRecipient,
  deletePaymentRecipient
} from '../Api/PaymentRecipient/PaymentRecipientApi';
import { addError } from './Error';
import { hasDataOrHigherPermissions, hasEngineerOrHigherPermissions, User } from './User';
import { DrawerData } from './Drawer';
import { CallResult } from '../Api/APIService';

export enum PaymentRecipientType {
  EMPLOYEE = 'Employee',
  SUB_AGENT = 'Sub-Agent',
  OTHER = 'Other'
}

export interface PaymentRecipient {
  id: string;
  name: string;
  type: PaymentRecipientType;
  createdAt: Date;
  updatedAt: Date;
  createdBy: User | null;
  modifiedBy: User | null;
  active: boolean;
  canBeDeleted: boolean;
  paymentsCount: number;
}

const paymentRecipientSubject = new BehaviorSubject<PaymentRecipient>({} as PaymentRecipient);
const paymentRecipientDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const paymentRecipientsSubject = new BehaviorSubject<PaymentRecipient[]>([] as PaymentRecipient[]);

const paymentRecipientLoadingSubject = new BehaviorSubject<boolean>(false);
const paymentRecipientsLoadingSubject = new BehaviorSubject<boolean>(false);
const updatingPaymentRecipientSubject = new BehaviorSubject<boolean>(false);

const clearError = () => addError('payment-recipient', undefined);

export const getPaymentRecipientSubject = (): BehaviorSubject<PaymentRecipient> => paymentRecipientSubject;
export const getPaymentRecipientLoadingSubject = (): BehaviorSubject<boolean> => paymentRecipientLoadingSubject;
export const getPaymentRecipientDetailSubject = (): BehaviorSubject<DrawerData | undefined> =>
  paymentRecipientDetailSubject;
export const getPaymentRecipientsSubject = (): BehaviorSubject<PaymentRecipient[]> => paymentRecipientsSubject;
export const getPaymentRecipientsLoadingSubject = (): BehaviorSubject<boolean> => paymentRecipientsLoadingSubject;
export const updatePaymentRecipientDetail = (paymentRecipientData: PaymentRecipient) =>
  paymentRecipientDetailSubject.next(paymentRecipientData);

export const getPaymentRecipientById = async (id: string): Promise<PaymentRecipient> => {
  const payment = paymentRecipientsSubject?.getValue()?.find((a) => a?.id === id);

  if (payment) {
    return payment;
  } else {
    const { data, error } = await getPaymentRecipient(id);
    if (error) addError('payment-recipient', error.message);
    if (data) return data;
  }

  return {} as PaymentRecipient;
};

export const fetchPaymentRecipients = async () => {
  if (!hasDataOrHigherPermissions()) {
    addError('payment-recipient', 'You do not have permission to view payment recipients.');
    return;
  }

  paymentRecipientsLoadingSubject.next(true);

  const { data, error } = await listPaymentRecipients();
  if (data) {
    clearError();
    paymentRecipientsSubject.next(data);
  }
  if (error) addError('payment-recipient', error.message);

  paymentRecipientsLoadingSubject.next(false);
};

export const updatePaymentRecipient = async (payment: PaymentRecipient, updateDrawer?: boolean) => {
  if (!hasDataOrHigherPermissions()) {
    addError('payment-recipient', 'You do not have permission to edit payment recipients.');
    return { data: null, error: { message: 'Permission denied' } } as CallResult<PaymentRecipient>;
  }
  updatingPaymentRecipientSubject.next(true);

  const { data, error } = await patchPaymentRecipient(payment);

  if (data) {
    clearError();
    fetchPaymentRecipients();
    const subject = getPaymentRecipientSubject().getValue();
    if (!updateDrawer || subject.id === data.id) paymentRecipientSubject.next(data);
    const detailSubject = getPaymentRecipientDetailSubject().getValue() as PaymentRecipient | undefined;
    if (updateDrawer || detailSubject?.id === data.id) paymentRecipientDetailSubject.next(data);
  }
  if (error) addError('payment-recipient', error.message);

  updatingPaymentRecipientSubject.next(false);
  return { data, error };
};

export const createNewPaymentRecipient = async (payment: PaymentRecipient) => {
  if (!hasDataOrHigherPermissions()) {
    addError('payment-recipient', 'You do not have permission to create payment recipients.');
    return;
  }
  updatingPaymentRecipientSubject.next(true);

  const { data, error } = await createPaymentRecipient(payment);

  if (data) {
    clearError();
    fetchPaymentRecipients();
  }
  if (error) addError('payment-recipient', error.message);

  updatingPaymentRecipientSubject.next(false);
  return { data, error };
};

export const removePaymentRecipient = async (id: string) => {
  if (!hasEngineerOrHigherPermissions()) {
    addError('payment-recipient', 'You do not have permission to delete payment recipients.');
    return;
  }
  updatingPaymentRecipientSubject.next(true);

  const { error } = await deletePaymentRecipient(id);

  if (!error) {
    clearError();
    const currentRecipients = paymentRecipientsSubject.getValue();
    paymentRecipientsSubject.next(currentRecipients.filter((pr) => pr.id !== id));
    const detailSubject = getPaymentRecipientDetailSubject().getValue() as PaymentRecipient | undefined;
    if (detailSubject?.id === id) paymentRecipientDetailSubject.next(undefined);
  }
  if (error) addError('payment-recipient', error.message);

  updatingPaymentRecipientSubject.next(false);
  return { error };
};
